/**
 * Экран достижений, серии и личных рекордов (ТЗ FR-EN-70, FR-GM-07, FR-GM-10).
 *
 * Показывает не только взятые рубежи, но и расстояние до остальных: «семь из
 * десяти» говорит человеку, что делать дальше, а «не получено» — ничего.
 *
 * Достижения бесплатны и не запираются подпиской (ТЗ FR-SUB-06): это отметка
 * о его труде, а не наша услуга.
 */

import type { Translate } from '@type2x/i18n';
import type {
  AchievementStatus,
  QuestStatus,
  SeasonStatus,
} from '@type2x/feature-personal-workspace';
import { Button, Card, ProgressBar } from '@type2x/ui';

import type { AchievementsView } from '../state/achievements.js';
import { AchievementIcon } from './AchievementIcon.js';

export interface AchievementsScreenProps {
  readonly t: Translate;
  readonly view: AchievementsView;
  readonly onBack: () => void;
}

/** Доля, дальше которой шкала не растёт: рубеж взят. */
function share(status: AchievementStatus): number {
  return status.target === 0 ? 1 : Math.min(status.current / status.target, 1);
}

/**
 * Достигнутое значение словами.
 *
 * Точность — доля, остальное — счётчики: «0.95 из 0.95» человек не читает.
 */
function amount(status: AchievementStatus): string {
  if (status.id !== 'accuracy-95') {
    return `${Math.round(status.current)} / ${status.target}`;
  }
  return `${Math.round(status.current * 100)}% / ${Math.round(status.target * 100)}%`;
}

/**
 * Рубеж: знак, название, условие, шкала и число (ТЗ UI-57).
 *
 * Полученное отличается от идущего сразу тремя вещами: галочкой, словом
 * «получено» и заполненной шкалой. Одной сменой цвета состояние не кодируется
 * — в оттенках серого оно должно читаться так же.
 *
 * Перебор показывается как есть: «17 / 10» — это правда, а шкала при этом
 * стоит на сотне, потому что дальше рубежа расти некуда.
 */
function AchievementItem({
  t,
  status,
}: {
  readonly t: Translate;
  readonly status: AchievementStatus;
}): React.JSX.Element {
  const unlocked = status.unlockedAt !== null;

  return (
    <li className={unlocked ? 't2x-achievement t2x-achievement--unlocked' : 't2x-achievement'}>
      <p className="t2x-achievement__title">
        <AchievementIcon id={status.id} />
        {unlocked && (
          <span className="t2x-achievement__check" aria-hidden="true">
            ✓
          </span>
        )}
        {t(`achievements.name.${status.id}`)}
        {unlocked && <span className="t2x-achievement__mark"> — {t('achievements.unlocked')}</span>}
      </p>
      <p className="t2x-muted t2x-legal">{t(`achievements.about.${status.id}`)}</p>
      <ProgressBar
        value={share(status)}
        label={t(`achievements.name.${status.id}`)}
        current={Math.round(status.id === 'accuracy-95' ? status.current * 100 : status.current)}
        total={Math.round(status.id === 'accuracy-95' ? status.target * 100 : status.target)}
      />
      <p className="t2x-muted t2x-legal">{amount(status)}</p>
    </li>
  );
}

/** Достигнутое по заданию словами: точность долей, остальное счётом. */
function questAmount(quest: QuestStatus): string {
  if (quest.id !== 'daily-accuracy') {
    return `${Math.round(quest.current)} / ${quest.target}`;
  }
  return `${Math.round(quest.current * 100)}% / ${Math.round(quest.target * 100)}%`;
}

function QuestItem({
  t,
  quest,
}: {
  readonly t: Translate;
  readonly quest: QuestStatus;
}): React.JSX.Element {
  return (
    <li className={quest.done ? 't2x-quest t2x-quest--done' : 't2x-quest'}>
      <p className="t2x-quest__title">
        {quest.done && (
          <span className="t2x-achievement__check" aria-hidden="true">
            ✓
          </span>
        )}
        {t(`quests.name.${quest.id}`)}
        {quest.done && <span className="t2x-achievement__mark"> — {t('quests.done')}</span>}
      </p>
      <ProgressBar
        value={quest.target === 0 ? 1 : Math.min(quest.current / quest.target, 1)}
        label={t(`quests.name.${quest.id}`)}
      />
      <p className="t2x-muted t2x-legal">{questAmount(quest)}</p>
    </li>
  );
}

/**
 * Сезонное испытание месяца (ТЗ FR-GM-07).
 *
 * Стоит между заданиями и серией: у дня, недели и месяца разный горизонт, и
 * порядок по нему же. Обратного отсчёта нет — сказано, сколько сделано, а не
 * сколько осталось до расплаты (ТЗ FR-GM-09).
 */
function SeasonBlock({
  t,
  season,
}: {
  readonly t: Translate;
  readonly season: SeasonStatus;
}): React.JSX.Element {
  const name = t(`season.month.${season.month}`);

  return (
    <section>
      <h3>{t('season.title')}</h3>
      <div className={season.done ? 't2x-quest t2x-quest--done' : 't2x-quest'}>
        <p className="t2x-quest__title">
          {season.done && (
            <span className="t2x-achievement__check" aria-hidden="true">
              ✓
            </span>
          )}
          {t('season.name', { month: name, year: season.year })}
          {season.done && <span className="t2x-achievement__mark"> — {t('quests.done')}</span>}
        </p>
        {/* Число цели приходит из самой цели: повторённое в словаре, оно
            разошлось бы с кодом при первой же правке. */}
        <p className="t2x-muted t2x-legal">
          {t(`season.challenge.${season.challenge}`, { target: season.target })}
        </p>
        <ProgressBar
          value={season.target === 0 ? 1 : Math.min(season.current / season.target, 1)}
          label={t(`season.challenge.${season.challenge}`, { target: season.target })}
          current={Math.round(season.current)}
          total={season.target}
        />
        <p className="t2x-muted t2x-legal">{`${Math.round(season.current)} / ${season.target}`}</p>
      </div>
      <p className="t2x-muted t2x-legal">{t('season.explain')}</p>
    </section>
  );
}

export function AchievementsScreen({
  t,
  view,
  onBack,
}: AchievementsScreenProps): React.JSX.Element {
  const { streak, records, statuses } = view;
  const earned = statuses.filter((status) => status.unlockedAt !== null).length;

  return (
    <Card title={t('achievements.title')}>
      <p className="t2x-muted">{t('achievements.summary', { earned, total: statuses.length })}</p>

      {/* Задания стоят первыми: человек открывает экран, чтобы понять, что
          делать сегодня, а не чтобы перечитать пройденное. */}
      <section>
        <h3>{t('quests.title')}</h3>
        <p className="t2x-muted t2x-legal">{t('quests.explain')}</p>

        {/* Дневные и недельные разделены: у них разный срок, и вперемешку
            непонятно, что успеть сегодня, а что за неделю (ТЗ UI-56). Карточка
            при этом одна — это по-прежнему один экран заданий. */}
        {(['day', 'week'] as const).map((period) => (
          <div key={period} className="t2x-quests__period">
            <h4 className="t2x-quests__title">{t(`quests.period.${period}`)}</h4>
            <ul className="t2x-quests">
              {view.quests
                .filter((quest) => quest.period === period)
                .map((quest) => (
                  <QuestItem key={quest.id} t={t} quest={quest} />
                ))}
            </ul>
          </div>
        ))}
      </section>

      <SeasonBlock t={t} season={view.season} />

      <section>
        <h3>{t('achievements.streak')}</h3>
        <p className="t2x-level">{t('achievements.streakDays', { days: streak.current })}</p>
        <p className="t2x-muted t2x-legal">
          {t('achievements.streakBest', { days: streak.longest, total: streak.total })}
        </p>
      </section>

      <section>
        <h3>{t('achievements.records')}</h3>
        <ul className="t2x-records">
          <li>{t('achievements.bestCpm', { cpm: Math.round(records.bestCpm) })}</li>
          <li>
            {t('achievements.bestAccuracy', { accuracy: Math.round(records.bestAccuracy * 100) })}
          </li>
          <li>{t('achievements.bestVerified', { wpm: Math.round(records.bestVerifiedWpm) })}</li>
          <li>{t('achievements.typed', { characters: records.typedCharacters })}</li>
        </ul>
      </section>

      <section>
        <h3>{t('achievements.list')}</h3>
        <ul className="t2x-achievements">
          {statuses.map((status) => (
            <AchievementItem key={status.id} t={t} status={status} />
          ))}
        </ul>
      </section>

      <Button variant="ghost" onClick={onBack}>
        {t('report.back')}
      </Button>
    </Card>
  );
}
